import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthHeader } from '@/hooks/useAuth';
import { getSellerHierarchies } from '@/lib/api/seller-hierarchy';
import { getWarehouseSellerMappings } from '@/lib/api/warehouse-seller-mapping';
import { getEcSellerSetting, updateEcSellerSetting } from '@/lib/api/ec-seller-setting';
import { Button } from '@/components/ui/button';
import type { SimpleAccount } from '@/lib/types';

/** 階層フィルタの「すべて」を表す値 */
const ALL_HIERARCHIES = '__all__';

/** この拠点種別の店舗は設定しなくても EC として扱われる（抽出 SQL と同じ規則） */
const ECOMMERCE_SITE_TYPE = 'Ecommerce';

interface EcSellerSettingProps {
  account: SimpleAccount;
}

/** 画面が扱う店舗1件。マスタ2本（店舗階層・倉庫店舗マッピング）を突き合わせて作る */
interface SellerRow {
  customerId: string;
  name: string;
  hierarchy: string;
  /** 紐づく倉庫の表示名。EC 倉庫はこの店舗から自動的に決まるため、選択の根拠として見せる */
  warehouses: string[];
  /** site_type が Ecommerce で、設定に関係なく EC になる店舗。右側に固定し、動かせない */
  autoEc: boolean;
}

/**
 * EC店舗設定（FKV3-12808 / Ecommerce の自動 EC 化は FKV3-12967）
 *
 * 分析用データで「EC」として扱う店舗を左右のボックスで振り分ける。
 * site_type が Ecommerce の店舗は常に EC なので右側に固定し、保存対象にも含めない。
 * Ecommerce へ移行していないアカウントのため、Store の店舗はここで EC に指定できる。
 */
export function EcSellerSetting({ account }: EcSellerSettingProps) {
  const { getAuthHeader } = useAuthHeader();

  const [sellers, setSellers] = useState<SellerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /** EC に指定している店舗（顧客採番ID） */
  const [ecIds, setEcIds] = useState<string[]>([]);
  /** 保存済みの状態。未保存の変更があるかの判定に使う */
  const [savedIds, setSavedIds] = useState<string[]>([]);
  const [updatedBy, setUpdatedBy] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  /** チェック中の店舗（左右で共有。移動時に該当側だけを見る） */
  const [checked, setChecked] = useState<string[]>([]);
  const [leftSearch, setLeftSearch] = useState('');
  const [rightSearch, setRightSearch] = useState('');
  const [hierarchyFilter, setHierarchyFilter] = useState(ALL_HIERARCHIES);

  // アカウント切替時に状態を捨てる（前アカウントの設定が残ると誤保存につながる）
  const accountCodeRef = useRef(account.code);
  if (account.code !== accountCodeRef.current) {
    accountCodeRef.current = account.code;
    setSellers([]);
    setEcIds([]);
    setSavedIds([]);
    setChecked([]);
    setLeftSearch('');
    setRightSearch('');
    setHierarchyFilter(ALL_HIERARCHIES);
    setError(null);
    setLoading(true);
    setSaving(false);
  }

  useEffect(() => {
    if (!account.code) return;
    let cancelled = false;
    const authHeaders = getAuthHeader();

    setLoading(true);
    Promise.all([
      getSellerHierarchies(account.code, authHeaders),
      getWarehouseSellerMappings(account.code, authHeaders),
      getEcSellerSetting(account.code, authHeaders),
    ])
      .then(([hierarchies, mappings, setting]) => {
        if (cancelled) return;

        // 1店舗に複数倉庫が紐づくことがあるため、店舗IDでまとめる
        const warehousesBySeller = new Map<string, string[]>();
        for (const m of mappings) {
          const list = warehousesBySeller.get(m.seller_customer_id) ?? [];
          list.push(`${m.warehouse_customer_id} ${m.warehouse_name}`);
          warehousesBySeller.set(m.seller_customer_id, list);
        }

        setSellers(
          hierarchies
            .filter((h): h is typeof h & { seller_customer_id: string } => !!h.seller_customer_id)
            .map((h) => ({
              customerId: h.seller_customer_id,
              name: h.seller_name ?? '',
              hierarchy: h.seller_hierarchy ?? '',
              warehouses: warehousesBySeller.get(h.seller_customer_id) ?? [],
              autoEc: h.site_type === ECOMMERCE_SITE_TYPE,
            })),
        );
        setEcIds(setting.seller_customer_ids);
        setSavedIds(setting.seller_customer_ids);
        setUpdatedBy(setting.updated_by);
        setUpdatedAt(setting.updated_at);
        setError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : '店舗情報の取得に失敗しました');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [account.code]);

  const ecIdSet = useMemo(() => new Set(ecIds), [ecIds]);
  const checkedSet = useMemo(() => new Set(checked), [checked]);

  /** 階層の選択肢。店舗が付いていない階層は出さない */
  const hierarchies = useMemo(
    () => Array.from(new Set(sellers.map((s) => s.hierarchy))).sort(),
    [sellers],
  );

  const matches = (seller: SellerRow, keyword: string) => {
    if (!keyword) return true;
    const lower = keyword.toLowerCase();
    return (
      seller.customerId.toLowerCase().includes(lower) ||
      seller.name.toLowerCase().includes(lower) ||
      seller.hierarchy.toLowerCase().includes(lower)
    );
  };

  const leftSellers = useMemo(
    () =>
      sellers.filter(
        (s) =>
          !s.autoEc &&
          !ecIdSet.has(s.customerId) &&
          matches(s, leftSearch) &&
          (hierarchyFilter === ALL_HIERARCHIES || s.hierarchy === hierarchyFilter),
      ),
    [sellers, ecIdSet, leftSearch, hierarchyFilter],
  );
  const rightSellers = useMemo(
    () => sellers.filter((s) => (s.autoEc || ecIdSet.has(s.customerId)) && matches(s, rightSearch)),
    [sellers, ecIdSet, rightSearch],
  );
  /** 右側のうち左へ戻せる店舗（Ecommerce は固定） */
  const movableRightSellers = useMemo(() => rightSellers.filter((s) => !s.autoEc), [rightSellers]);

  const isDirty = useMemo(
    () => JSON.stringify([...ecIds].sort()) !== JSON.stringify([...savedIds].sort()),
    [ecIds, savedIds],
  );

  const toggleCheck = (customerId: string) => {
    setChecked((prev) =>
      prev.includes(customerId) ? prev.filter((id) => id !== customerId) : [...prev, customerId],
    );
  };

  /** 指定した店舗を EC へ入れる／から外す。移動した店舗のチェックは外す */
  const move = (targets: SellerRow[], toEc: boolean) => {
    const ids = targets.map((s) => s.customerId);
    if (ids.length === 0) return;
    setEcIds((prev) =>
      toEc ? Array.from(new Set([...prev, ...ids])) : prev.filter((id) => !ids.includes(id)),
    );
    setChecked((prev) => prev.filter((id) => !ids.includes(id)));
  };

  const handleSave = async () => {
    // 保存はこのアカウントに対する操作。完了までにアカウントが切り替わったら
    // 結果を画面へ書き戻さない。書き戻すと次の保存で前アカウントのIDを
    // 別アカウントへ丸ごと上書きしてしまう（PUT は全置換）
    const requestCode = account.code;
    try {
      setSaving(true);
      setError(null);
      // Ecommerce へ移った店舗が過去の設定に残っていても、保存のたびに落として設定を Store だけにする
      const autoEcIds = new Set(sellers.filter((s) => s.autoEc).map((s) => s.customerId));
      const saved = await updateEcSellerSetting(
        requestCode,
        { seller_customer_ids: ecIds.filter((id) => !autoEcIds.has(id)) },
        getAuthHeader(),
      );
      // トーストは画面から独立した通知なので、切り替わっていてもどのアカウントの
      // 結果なのかが分かる形で出す
      toast.success(`${requestCode} のEC店舗設定を保存しました`);
      if (accountCodeRef.current !== requestCode) return;
      setSavedIds(saved.seller_customer_ids);
      setEcIds(saved.seller_customer_ids);
      setUpdatedBy(saved.updated_by);
      setUpdatedAt(saved.updated_at);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'EC店舗設定の保存に失敗しました';
      toast.error(`${requestCode}: ${message}`);
      if (accountCodeRef.current !== requestCode) return;
      setError(message);
    } finally {
      if (accountCodeRef.current === requestCode) setSaving(false);
    }
  };

  const renderBox = (
    title: string,
    rows: SellerRow[],
    search: string,
    onSearch: (value: string) => void,
    withHierarchyFilter: boolean,
  ) => (
    <Paper
      variant="outlined"
      sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}
    >
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {title}（{rows.length}件）
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder="店舗ID・店舗名・階層で検索"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            inputProps={{ 'aria-label': `${title}を検索` }}
            sx={{ flex: 1 }}
          />
          {withHierarchyFilter && (
            <TextField
              select
              size="small"
              value={hierarchyFilter}
              onChange={(e) => setHierarchyFilter(e.target.value)}
              label="階層で絞る"
              sx={{ minWidth: 180 }}
            >
              <MenuItem value={ALL_HIERARCHIES}>すべて</MenuItem>
              {hierarchies.map((h) => (
                <MenuItem key={h} value={h}>
                  {h}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>
      </Box>
      <List dense sx={{ height: 380, overflow: 'auto', py: 0 }}>
        {rows.map((seller) => (
          <ListItemButton
            key={seller.customerId}
            onClick={() => toggleCheck(seller.customerId)}
            disabled={seller.autoEc}
            dense
          >
            <Checkbox
              edge="start"
              tabIndex={-1}
              disableRipple
              checked={checkedSet.has(seller.customerId)}
              disabled={seller.autoEc}
              inputProps={{ 'aria-label': `${seller.customerId} ${seller.name}` }}
            />
            <ListItemText
              primary={`${seller.customerId} ${seller.name}`}
              secondary={[
                seller.autoEc ? 'Ecommerce（自動でEC）' : null,
                seller.warehouses.length > 0 ? seller.warehouses.join(' / ') : '（倉庫なし）',
              ]
                .filter(Boolean)
                .join(' ・ ')}
              primaryTypographyProps={{ variant: 'body2', noWrap: true }}
              secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
            />
          </ListItemButton>
        ))}
        {rows.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              該当する店舗がありません
            </Typography>
          </Box>
        )}
      </List>
    </Paper>
  );

  const checkedIn = (rows: SellerRow[]) => rows.filter((s) => checkedSet.has(s.customerId));

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1 }}>
          EC店舗設定
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          分析用データで「EC」として扱う店舗を選びます。選んだ店舗に紐づく倉庫が EC
          倉庫として扱われ、店舗投入日の集計から外れます。拠点種別が Ecommerce
          の店舗は設定しなくても EC として扱われるため、右側に固定で表示しています。EC
          店舗が1件も無い場合、商品マスタ抽出の EC
          由来の列（EC販売開始日など）はすべて空欄になります。
        </Typography>

        {error && (
          <MuiAlert severity="error" sx={{ mb: 2 }}>
            {error}
          </MuiAlert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>店舗情報を読み込み中...</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>
              {renderBox('実店舗', leftSellers, leftSearch, setLeftSearch, true)}

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="選択した店舗をECにする"
                  disabled={checkedIn(leftSellers).length === 0}
                  onClick={() => move(checkedIn(leftSellers), true)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="選択した店舗を実店舗に戻す"
                  disabled={checkedIn(movableRightSellers).length === 0}
                  onClick={() => move(checkedIn(movableRightSellers), false)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="表示中の店舗をすべてECにする"
                  disabled={leftSellers.length === 0}
                  onClick={() => move(leftSellers, true)}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  aria-label="表示中の店舗をすべて実店舗に戻す"
                  disabled={movableRightSellers.length === 0}
                  onClick={() => move(movableRightSellers, false)}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
              </Box>

              {renderBox('EC店舗', rightSellers, rightSearch, setRightSearch, false)}
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 2,
                gap: 2,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {updatedAt
                  ? `最終更新: ${new Date(updatedAt).toLocaleString('ja-JP')}　${updatedBy ?? ''}`
                  : '未設定'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {isDirty && (
                  <Typography variant="caption" className="text-amber-600">
                    未保存の変更があります
                  </Typography>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!isDirty || saving}
                  onClick={() => {
                    setEcIds(savedIds);
                    setChecked([]);
                  }}
                >
                  元に戻す
                </Button>
                <Button size="sm" disabled={!isDirty || saving} onClick={handleSave}>
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  保存
                </Button>
              </Box>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
}
